import {
  forwardRef,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_arrow_right } from '@gn-audio/icons';

import { JabraIcon } from '../../JabraIcon/JabraIcon';

type MenuItemProps = {
  title: string;
  onClick?: () => void;
  onMouseOver?: (e: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
  onMouseOut?: (e: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
  linkField?: LinkField;
  isActive?: boolean;
  autoFocus?: boolean;
  additionalClass?: string;
};

export interface FocusableRef {
  focus: () => void;
}

export const DesktopMenuItem = forwardRef<FocusableRef, MenuItemProps>(
  (
    {
      title,
      onClick,
      onMouseOver,
      onMouseOut,
      linkField,
      isActive,
      additionalClass = '',
    }: MenuItemProps,
    ref
  ) => {
    const commonProps = {
      className: `flex relative flex-1 justify-between py-2.5 cursor-pointer group ${additionalClass} ${
        isActive && 'active'
      }`,
      onMouseOver,
      onMouseOut,
    };

    const linkRef = useRef<HTMLAnchorElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const arrowElement = (
      <JabraIcon
        Icon={icon_arrow_right}
        className={classNames(
          'icon yellow mx-1 h-5 transition-all duration-300 ease-linear group-hover:translate-x-0 group-hover:opacity-100',
          isActive
            ? 'translate-x-0 opacity-100 group-focus:translate-x-0 group-focus:opacity-100'
            : '-translate-x-full opacity-0'
        )}
      />
    );

    const focus = useCallback(() => {
      linkRef.current?.focus();
      buttonRef.current?.focus();
    }, []);

    useImperativeHandle(ref, () => ({
      focus,
    }));

    return linkField ? (
      <a
        href={linkField?.value?.href}
        {...commonProps}
        className={commonProps.className}
        ref={linkRef}
      >
        <span>{title}</span>
        {arrowElement}
      </a>
    ) : (
      <button
        ref={buttonRef}
        {...commonProps}
        onClick={onClick}
        className={classNames(commonProps.className, 'w-full items-center')}
      >
        <span className="flex-grow text-left">{title}</span>
        {arrowElement}
      </button>
    );
  }
);

DesktopMenuItem.displayName = 'DesktopMenuItem';
